import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  X, 
  Plus, 
  AlertCircle,
  Slack,
  Building,
  Phone,
  Mail,
  ExternalLink
} from 'lucide-react';
import { UnmappedSource, CustomerStatus, SourceType } from '../types/api';
import { apiClient } from '../lib/apiClient';
import EnforcementModal from './EnforcementModal';
// import { toast } from 'sonner';

interface CreateCustomerModalProps {
  source?: UnmappedSource; // Make source optional
  suggestedName?: string; // Make suggestedName optional
  onClose: () => void;
  onSuccess: () => void;
}

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  source,
  suggestedName,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: suggestedName || '',
    domains: [] as string[],
    status: 'prospect' as CustomerStatus,
    mrr: undefined as number | undefined,
    industry: '',
    company_size: '',
    contract_value: undefined as number | undefined,
    contract_start: '',
    contract_end: '',
    crm_id: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [showEnforcementModal, setShowEnforcementModal] = useState(false);
  const [enforcementError, setEnforcementError] = useState<any>(null);

  const getSourceIcon = (sourceType: SourceType) => {
    switch (sourceType) {
      case 'slack_channel': return <Slack className="w-4 h-4 text-purple-600" />;
      case 'crm_account': return <Building className="w-4 h-4 text-blue-600" />;
      case 'gong_call': return <Phone className="w-4 h-4 text-green-600" />;
      case 'email_domain': return <Mail className="w-4 h-4 text-orange-600" />;
      default: return <ExternalLink className="w-4 h-4" />;
    }
  };

  const addDomain = () => {
    if (domainInput.trim() && !formData.domains.includes(domainInput.trim())) {
      setFormData(prev => ({
        ...prev,
        domains: [...prev.domains, domainInput.trim()]
      }));
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setFormData(prev => ({
      ...prev,
      domains: prev.domains.filter(d => d !== domain)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Customer name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Create the customer
      const newCustomer = await apiClient.createCustomer({
        name: formData.name.trim(),
        domains: formData.domains,
        status: formData.status,
        mrr: formData.mrr,
        industry: formData.industry || undefined,
        company_size: formData.company_size || undefined,
        contract_value: formData.contract_value,
        contract_start: formData.contract_start ? new Date(formData.contract_start) : undefined,
        contract_end: formData.contract_end ? new Date(formData.contract_end) : undefined,
        crm_id: formData.crm_id || undefined
      });

      console.log('CreateCustomerModal - newCustomer response:', newCustomer);
      console.log('CreateCustomerModal - newCustomer.customer_id:', newCustomer?.customer_id);

      // Map the source to the newly created customer (only if source exists)
      if (source) {
        if (!newCustomer?.customer_id) {
          throw new Error('Customer created but customer_id is missing from response');
        }
        
        console.log('CreateCustomerModal - Mapping source to customer:', {
          source_type: source.source_type,
          source_id: source.source_id,
          customer_id: newCustomer.customer_id
        });
        
        await apiClient.mapSourceToCustomer(source.source_type, source.source_id, newCustomer.customer_id);
        console.log('Customer created and source mapped successfully!');
      } else {
        console.log('Customer created successfully!');
      }
      onSuccess();
      
    } catch (err: any) {
      console.log('Full error object:', err);
      console.log('Error response:', err.response);
      console.log('Error data:', err.response?.data);
      
      // Check if this is a plan enforcement error
      if (err.response && err.response.status === 403) {
        const errorData = err.response.data;
        console.log('Error data structure:', JSON.stringify(errorData, null, 2));
        
        // Handle multiple possible error structures
        let enforcementError = null;
        
        // Try different possible structures
        if (errorData.error) {
          enforcementError = errorData.error;
        } else if (errorData.detail) {
          enforcementError = errorData.detail;
        } else if (typeof errorData === 'string') {
          enforcementError = { message: errorData };
        } else if (errorData.message) {
          enforcementError = errorData;
        } else {
          enforcementError = errorData;
        }
        
        console.log('Processed enforcement error:', enforcementError);
        
        // Check if this is a plan limit error
        if (enforcementError && (
          enforcementError.error === 'plan_limit_exceeded' || 
          enforcementError.message?.includes('Limit of') ||
          enforcementError.message?.includes('plan limit') ||
          enforcementError.message?.includes('upgrade your plan')
        )) {
          console.log('Setting enforcement error:', enforcementError);
          setEnforcementError(enforcementError);
          setShowEnforcementModal(true);
        } else {
          // Show the actual error message instead of generic text
          const errorMessage = enforcementError?.message || enforcementError?.detail || JSON.stringify(enforcementError) || 'Failed to create customer';
          console.log('Setting regular error message:', errorMessage);
          setError(errorMessage);
          console.error('Error creating customer:', errorMessage);
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create customer';
        setError(errorMessage);
        console.error('Error creating customer:', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border-0 bg-white dark:bg-gray-800 backdrop-blur-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6 border-b border-gray-100 dark:border-gray-700">
          <div>
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create New Account</CardTitle>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Create an account profile and link this source</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent>
          {/* Source Information - Only show if source exists */}
          {source && (
            <div className="mb-8 p-5 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white dark:bg-gray-700 rounded-lg shadow-sm">
                    {getSourceIcon(source.source_type)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{source.source_name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {source.source_type.replace('_', ' ')} Source
                    </p>
                  </div>
                </div>
                {source.confidence_score && (
                  <Badge variant="secondary" className="bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800">
                    {source.confidence_score}% confidence
                  </Badge>
                )}
              </div>
              {source.suggested_customer_name && (
                <div className="text-sm text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/20 px-3 py-2 rounded-lg">
                  💡 <strong>Suggested name:</strong> {source.suggested_customer_name}
                </div>
              )}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                Basic Information
              </h4>
              
              {/* Customer Name */}
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter customer name"
                  required
                  className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Email Domains */}
              <div>
                <Label htmlFor="domains" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Domains
                </Label>
                <div className="flex gap-2 mb-2 mt-1">
                  <Input
                    id="domains"
                    type="text"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    placeholder="example.com"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                    className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Button type="button" onClick={addDomain} size="sm" className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.domains.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.domains.map((domain) => (
                      <Badge key={domain} variant="secondary" className="cursor-pointer hover:bg-red-100 hover:text-red-800" onClick={() => removeDomain(domain)}>
                        {domain} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </Label>
                <Select value={formData.status} onValueChange={(value: CustomerStatus) => setFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">🔍 Prospect</SelectItem>
                    <SelectItem value="active">✅ Active</SelectItem>
                    <SelectItem value="churned">❌ Churned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Financial Information Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                Financial Information
              </h4>
              
              {/* MRR */}
              <div>
                <Label htmlFor="mrr" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Monthly Recurring Revenue
                </Label>
                <Input
                  id="mrr"
                  type="number"
                  value={formData.mrr || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, mrr: e.target.value ? Number(e.target.value) : undefined }))}
                  placeholder="0"
                  min="0"
                  className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Contract Value and CRM ID */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contract_value" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Contract Value
                  </Label>
                  <Input
                    id="contract_value"
                    type="number"
                    value={formData.contract_value || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_value: e.target.value ? Number(e.target.value) : undefined }))}
                    placeholder="0"
                    min="0"
                    className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <Label htmlFor="crm_id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    CRM ID
                  </Label>
                  <Input
                    id="crm_id"
                    type="text"
                    value={formData.crm_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, crm_id: e.target.value }))}
                    placeholder="SF-001"
                    className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Contract Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contract_start" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Contract Start
                  </Label>
                  <Input
                    id="contract_start"
                    type="date"
                    value={formData.contract_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_start: e.target.value }))}
                    className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <Label htmlFor="contract_end" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Contract End
                  </Label>
                  <Input
                    id="contract_end"
                    type="date"
                    value={formData.contract_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_end: e.target.value }))}
                    className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Company Information Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                Company Information
              </h4>
              
              {/* Industry and Company Size */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="industry" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Industry
                  </Label>
                  <Input
                    id="industry"
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    placeholder="Technology"
                    className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <Label htmlFor="company_size" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company Size
                  </Label>
                  <Input
                    id="company_size"
                    type="text"
                    value={formData.company_size}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_size: e.target.value }))}
                    placeholder="1-50 employees"
                    className="mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="text-red-700 text-sm flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span>{error}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose} 
                className="flex-1 py-3 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading} 
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Creating Customer...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    <span>{source ? 'Create & Link Account' : 'Create Account'}</span>
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Enforcement Modal */}
      <EnforcementModal
        isOpen={showEnforcementModal}
        onClose={() => setShowEnforcementModal(false)}
        error={enforcementError}
      />
    </div>
  );
};

export default CreateCustomerModal;
